import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import type { GridHighlight } from '../measures/measures-highlight.util';
import type { FrequencyTableParams } from './frequency-table.model';

@Component({
  selector: 'dojo-frequency-table',
  standalone: true,
  templateUrl: './frequency-table.component.html',
  styleUrl: './frequency-table.component.scss',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrequencyTableComponent {
  readonly params = input.required<FrequencyTableParams>();
  readonly gridHighlight = input<GridHighlight | null>(null);

  readonly valueHover = output<number | null>();

  readonly entries = computed(() => {
    const data = this.params().data;
    if (data.length === 0) {
      return [];
    }

    const highlight = this.gridHighlight();

    const freqMap = new Map<number, number>();
    for (const value of data) {
      freqMap.set(value, (freqMap.get(value) ?? 0) + 1);
    }

    const sortedValues = [...freqMap.keys()].sort((a, b) => a - b);

    return sortedValues.map((value) => ({
      value,
      displayValue: fmt(value),
      frequency: freqMap.get(value)!,
      color: getHighlightColor(value, highlight),
    }));
  });

  onHover(value: number | null): void {
    this.valueHover.emit(value);
  }
}

function fmt(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(2)).toString();
}

function getHighlightColor(value: number, highlight?: GridHighlight | null): string | null {
  if (!highlight) {
    return null;
  }

  const exactColor = highlight.exactValues.get(value);
  if (exactColor) {
    return exactColor;
  }

  for (const range of highlight.ranges) {
    if (value >= range.min && value <= range.max) {
      return range.color;
    }
  }

  if (highlight.highlightAllColor) {
    return highlight.highlightAllColor;
  }

  return null;
}
